import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  mergeOperationsResults,
  getOperationsAndPageInfo,
} from './useOperations.helpers';
import { TransactionTabName } from '@/features/Profile/sections/Transactions/Transactions.types';
import {
  GET_PAID_ADS_TRANSACTIONS_LIST,
  GET_PAYMENTS_LIST,
  GET_TRANSACTIONS_LIST,
  OperationsResponse,
  OperationsVariables,
} from '@/features/Profile/sections/Transactions/api';
import { DEFAULT_PAGE_SIZE } from '@/features/Profile/sections/Transactions/Transactions.constants';
import { useOnlineStatus } from '@/features/Profile/core/hooks';

const OPERATIONS_QUERY = {
  [TransactionTabName.Account]: GET_TRANSACTIONS_LIST,
  [TransactionTabName.Ads]: GET_PAYMENTS_LIST,
  [TransactionTabName.PaidAds]: GET_PAID_ADS_TRANSACTIONS_LIST,
};

export function useOperations(activeTab: TransactionTabName) {
  const { t } = useTranslation();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isOnline = useOnlineStatus();

  const { data, loading, fetchMore } = useQuery<
    OperationsResponse,
    OperationsVariables
  >(OPERATIONS_QUERY[activeTab], {
    fetchPolicy: 'cache-and-network',
    variables: { first: DEFAULT_PAGE_SIZE, after: null },
  });

  const { operations, pageInfo } = getOperationsAndPageInfo(activeTab, data, t);
  const { endCursor, hasNextPage = false } = pageInfo ?? {};
  const canLoadMore = hasNextPage && isOnline;

  const loadMore = useCallback(() => {
    if (loading || isLoadingMore || !canLoadMore || !endCursor) {
      return;
    }

    setIsLoadingMore(true);
    fetchMore({
      variables: { after: endCursor },
      updateQuery: (prevResult, options) =>
        mergeOperationsResults(activeTab, prevResult, options),
    }).finally(() => setIsLoadingMore(false));
  }, [activeTab, canLoadMore, endCursor, fetchMore, isLoadingMore, loading]);

  return {
    operations,
    isLoading: loading,
    isLoadingMore,
    hasNextPage,
    canLoadMore,
    loadMore,
  };
}
